import { Check, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { QuickLink } from '@/api/uiPreferences';
import type { FeatureFlags } from '@/config/feature-flags';
import { listAvailableQuickLinkPages, type QuickLinkPage } from '@/config/quickLinkPages';
import { usePermissions } from '@/hooks/usePermissions';

const MAX_QUICK_LINKS = 12;

interface QuickLinksEditorProps {
  links: QuickLink[];
  onChange: (next: QuickLink[]) => void;
  flags?: FeatureFlags | null;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ql-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function QuickLinksEditor({ links, onChange, flags }: QuickLinksEditorProps) {
  const { hasPermission } = usePermissions();
  const options = useMemo(
    () => listAvailableQuickLinkPages(hasPermission, flags),
    [hasPermission, flags]
  );

  const sorted = useMemo(() => [...links].sort((a, b) => a.order - b.order), [links]);

  const reindex = (next: QuickLink[]): QuickLink[] =>
    next.map((item, idx) => ({ ...item, order: idx }));

  const handleAdd = (page: QuickLinkPage) => {
    if (sorted.length >= MAX_QUICK_LINKS) return;
    if (sorted.some((link) => link.path === page.pathTemplate)) return;

    const newLink: QuickLink = {
      id: generateId(),
      label: page.label,
      path: page.pathTemplate,
      icon: '',
      order: sorted.length,
    };
    onChange(reindex([...sorted, newLink]));
  };

  const handleUpdate = (id: string, patch: Partial<QuickLink>) => {
    onChange(reindex(sorted.map((l) => (l.id === id ? { ...l, ...patch } : l))));
  };

  const handleRemove = (id: string) => {
    onChange(reindex(sorted.filter((l) => l.id !== id)));
  };

  const handleMove = (id: string, direction: -1 | 1) => {
    const idx = sorted.findIndex((l) => l.id === id);
    if (idx === -1) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sorted.length) return;
    const next = [...sorted];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(reindex(next));
  };

  const knownPaths = new Set(options.map((o) => o.pathTemplate));
  const optionByPath = new Map(options.map((option) => [option.pathTemplate, option]));
  const selectedPaths = new Set(sorted.map((link) => link.path));
  const hasReachedLimit = sorted.length >= MAX_QUICK_LINKS;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Quick Links</h4>
          <p className="mt-1 text-xs leading-5 text-gray-500">
            Pick shortcuts once, then rename or reorder them below.
          </p>
        </div>
        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
          {sorted.length}/{MAX_QUICK_LINKS}
        </span>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-[#FDF9F2]/70 p-3">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Selected</p>
          {sorted.length > 0 && <p className="text-xs text-gray-400">Shown in this order</p>}
        </div>
        {sorted.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-5 text-center text-sm text-gray-500">
            No shortcuts yet. Add from the grid below.
          </div>
        )}
        <div className="space-y-2">
          {sorted.map((link, idx) => {
            const catalog = optionByPath.get(link.path);
            const Icon = catalog?.icon;
            const isUnknown = !knownPaths.has(link.path);

            return (
              <div
                key={link.id}
                className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-2.5 shadow-sm"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600">
                  {Icon ? <Icon className="h-4 w-4" /> : <span className="text-xs">?</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <input
                    type="text"
                    value={link.label}
                    onChange={(event) => handleUpdate(link.id, { label: event.target.value })}
                    maxLength={60}
                    placeholder="Label"
                    className="h-7 w-full rounded-md border-0 bg-transparent px-0 text-sm font-medium text-gray-900 outline-none focus:ring-0"
                    aria-label="Label"
                  />
                  <p className="truncate text-xs text-gray-400">
                    {isUnknown ? `Removed page: ${link.path}` : catalog?.label}
                  </p>
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => handleMove(link.id, -1)}
                    disabled={idx === 0}
                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMove(link.id, 1)}
                    disabled={idx === sorted.length - 1}
                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(link.id)}
                    className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"
                    aria-label="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Add shortcuts</p>
        <div className="grid grid-cols-2 gap-2">
          {options.map((page) => {
            const Icon = page.icon;
            const isSelected = selectedPaths.has(page.pathTemplate);
            const isDisabled = isSelected || hasReachedLimit;

            return (
              <button
                key={page.key}
                type="button"
                disabled={isDisabled}
                onClick={() => handleAdd(page)}
                className="group flex min-h-[72px] flex-col items-start justify-between rounded-2xl border border-gray-200 bg-white p-3 text-left transition-colors hover:border-[#00625B] hover:bg-[#FDF9F2] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div className="flex w-full items-start justify-between gap-2">
                  <Icon className="h-4 w-4 text-gray-500 group-hover:text-[#00625B]" />
                  {isSelected ? (
                    <Check className="h-4 w-4 text-[#00625B]" />
                  ) : (
                    <Plus className="h-4 w-4 text-gray-400 group-hover:text-[#00625B]" />
                  )}
                </div>
                <span className="mt-3 line-clamp-2 text-sm font-medium text-gray-900">
                  {page.label}
                </span>
              </button>
            );
          })}
        </div>
        {hasReachedLimit && (
          <p className="text-xs text-gray-500">Remove a shortcut before adding another.</p>
        )}
      </div>
    </div>
  );
}
